/* eslint-disable global-require */

import * as React from "react";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { capitalize } from "@utdanningsdirektoratet/utils/string";
import { warn } from "@utdanningsdirektoratet/utils/assert";
import { SmallIcon, WideIcon, LargeIcon, Color } from "./types";
import styles from "./Icon.module.less";

type IconSizeTypes =
  | {
      icon: SmallIcon | "none";
      type?: "small";
    }
  | {
      icon: WideIcon;
      type: "wide";
    }
  | {
      icon: LargeIcon;
      type: "large";
    };

interface IconProps extends React.HTMLAttributes<HTMLElement> {
  color?: Color;
  placement?: "after" | "before";
  className?: string;
}

const getSvg = (icon: string, type: string): { __html: string } | undefined => {
  try {
    switch (type) {
      case "large":
        return { __html: require(`./static/icons/32x32/${icon}.svg`) }; // eslint-disable-line import/no-dynamic-require
      case "wide":
        return { __html: require(`./static/icons/16x32/${icon}.svg`) }; // eslint-disable-line import/no-dynamic-require
      case "small":
      default:
        return { __html: require(`./static/icons/16x16/${icon}.svg`) }; // eslint-disable-line import/no-dynamic-require
    }
  } catch {
    warn(`Icon ${icon} not found for type ${type}`);
    return { __html: "" };
  }
};

const Icon = React.forwardRef<HTMLElement, IconProps & IconSizeTypes>(
  ({ icon, type = "small", placement, className, color, ...rest }, ref) => {
    if (icon === "none") return null;

    const iconClass = cls(
      {
        [styles.icon]: true,
        [styles.iconSmall]: type === "small",
        [styles.iconWide]: type === "wide",
        [styles.iconLarge]: type === "large",
        [styles.iconBefore]: placement === "before",
        [styles.iconAfter]: placement === "after",
        [styles.iconAnimated]: icon === "spinner",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [styles[`icon${capitalize(color)}`]]: !!color,
      },
      className
    );

    return (
      <i
        {...rest}
        ref={ref}
        aria-hidden={!rest["aria-label"]}
        className={iconClass}
        dangerouslySetInnerHTML={getSvg(icon, type)} // eslint-disable-line react/no-danger
      />
    );
  }
);

export default Icon;
